import * as types from "../constants/userConstants";
import { LOGOUT } from "../constants/authConstants";

const initialState = {
  user: {},
  publicUsers: [],
  publicUserProfile: {},
  followingUsers: [],
  totalFollowingUsers: 0,
  followersUsers: [],
  totalFollowersUsers: 0,
  isFollowing: null,
  userError: null,
  mutualSkills: [],
  mutualSkillsLoading: false,
  mutualCommunities: [],
  mutualCommunitiesLoading: false,
  privacySettings: null,
  blockedUsers: [],
  blockedUsersList: [], // Full user objects
  receivedProfileViewRequests: [],
  totalReceivedProfileViewRequests: 0,
  sentProfileViewRequests: [],
  totalSentProfileViewRequests: 0,
  receivedFollowRequests: [],
  totalReceivedFollowRequests: 0,
  sentFollowRequests: [],
  totalSentFollowRequests: 0,
  notifications: [],
  totalNotifications: 0,
  unreadNotificationCount: 0,
};

const userReducer = (state = initialState, action) => {
  // eslint-disable-next-line no-unused-vars
  const { type, payload } = action;
  // skip is accessed via payload.skip and action.skip, not destructured

  switch (type) {
    case LOGOUT:
      // Don't clear publicUserProfile on logout - it's public data that should remain visible
      // Only clear user-specific data (following, followers, requests, etc.)
      return {
        ...state,
        user: {},
        publicUsers: [],
        // publicUserProfile: {}, // Keep public profile data - it's viewable even when logged out
        followingUsers: [],
        followersUsers: [],
        totalFollowingUsers: 0,
        totalFollowersUsers: 0,
        isFollowing: null,
        userError: null,
        // Clear user-specific requests
        receivedProfileViewRequests: [],
        totalReceivedProfileViewRequests: 0,
        sentProfileViewRequests: [],
        totalSentProfileViewRequests: 0,
        receivedFollowRequests: [],
        totalReceivedFollowRequests: 0,
        sentFollowRequests: [],
        totalSentFollowRequests: 0,
      };

    case types.GET_USER_SUCCESS:
      return { ...state, user: payload, userError: null };

    case types.GET_USER_FAIL:
      return { ...state, userError: payload };

    case types.GET_PUBLIC_USERS_SUCCESS:
      return {
        ...state,
        publicUsers: payload || [],
        userError: null,
      };

    case types.GET_PUBLIC_USERS_FAIL:
      return { ...state, userError: payload };

    case types.GET_PUBLIC_USER_PROFILE_SUCCESS:
      // Only update if payload is valid (has _id)
      if (!payload || !payload._id) {
        return state; // Don't update state with invalid data
      }
      
      return {
        ...state,
        publicUserProfile: payload,
        userError: null,
        isFollowing: payload?.isFollowing ?? null,
      };

    case types.GET_PUBLIC_USER_PROFILE_FAIL:
      return { ...state, userError: payload };

    case types.CLEAR_PUBLIC_USER_PROFILE:
      return {
        ...state,
        publicUserProfile: {},
        isFollowing: null,
        mutualSkills: [],
        mutualCommunities: [],
      };

    case types.CHANGE_FOLLOW_STATUS_SUCCESS:
      return {
        ...state,
        isFollowing: payload ? payload.isFollowing : null,
        userError: null,
      };

    case types.CHANGE_FOLLOW_STATUS_FAIL:
      return { ...state, userError: payload };

    case types.GET_FOLLOWING_USERS_SUCCESS:
      if (payload.page === 1) {
        return {
          ...state,
          followingUsers: payload.users || payload,
          totalFollowingUsers: payload.totalUsers || payload.length,
          userError: null,
        };
      } else {
        return {
          ...state,
          followingUsers: [
            ...state.followingUsers,
            ...(payload.users || payload),
          ],
          totalFollowingUsers: payload.totalUsers || payload.length,
          userError: null,
        };
      }

    case types.GET_FOLLOWING_USERS_FAIL:
      return { ...state, userError: payload };

    case types.CLEAR_FOLLOWING_USERS:
      return {
        ...state,
        followingUsers: [],
        totalFollowingUsers: 0,
        userError: null,
      };

    case types.SEARCH_FOLLOWING_USERS_REQUEST:
      return {
        ...state,
        userError: null,
      };

    case types.SEARCH_FOLLOWING_USERS_SUCCESS:
      return {
        ...state,
        followingUsers: payload.users || payload,
        totalFollowingUsers: payload.totalUsers || payload.length,
        userError: null,
      };

    case types.SEARCH_FOLLOWING_USERS_FAIL:
      return { ...state, userError: payload };

    case types.REMOVE_FOLLOWING_USER:
      return {
        ...state,
        followingUsers: state.followingUsers.filter(user => user._id !== payload),
        totalFollowingUsers: Math.max(0, state.totalFollowingUsers - 1),
        userError: null,
      };

    case types.GET_FOLLOWERS_USERS_SUCCESS:
      if (payload.page === 1) {
        return {
          ...state,
          followersUsers: payload.users || payload,
          totalFollowersUsers: payload.totalUsers || payload.length,
          userError: null,
        };
      } else {
        return {
          ...state,
          followersUsers: [
            ...state.followersUsers,
            ...(payload.users || payload),
          ],
          totalFollowersUsers: payload.totalUsers || payload.length,
          userError: null,
        };
      }

    case types.GET_FOLLOWERS_USERS_FAIL:
      return { ...state, userError: payload };

    case types.CLEAR_FOLLOWERS_USERS:
      return {
        ...state,
        followersUsers: [],
        totalFollowersUsers: 0,
        userError: null,
      };

    case types.SEARCH_FOLLOWERS_USERS_REQUEST:
      return {
        ...state,
        userError: null,
      };

    case types.SEARCH_FOLLOWERS_USERS_SUCCESS:
      return {
        ...state,
        followersUsers: payload.users || payload,
        totalFollowersUsers: payload.totalUsers || payload.length,
        userError: null,
      };

    case types.SEARCH_FOLLOWERS_USERS_FAIL:
      return { ...state, userError: payload };

    case types.REMOVE_FOLLOWERS_USER:
      return {
        ...state,
        followersUsers: state.followersUsers.filter(user => user._id !== payload),
        totalFollowersUsers: Math.max(0, state.totalFollowersUsers - 1),
        userError: null,
      };

    case types.GET_MUTUAL_SKILLS_REQUEST:
      return {
        ...state,
        mutualSkillsLoading: true,
        userError: null,
      };

    case types.GET_MUTUAL_SKILLS_SUCCESS:
      return {
        ...state,
        mutualSkills: payload.mutualSkills || [],
        mutualSkillsLoading: false,
        userError: null,
      };

    case types.GET_MUTUAL_SKILLS_FAIL:
      return {
        ...state,
        mutualSkillsLoading: false,
        userError: payload,
      };

    case types.GET_MUTUAL_COMMUNITIES_REQUEST:
      return {
        ...state,
        mutualCommunitiesLoading: true,
        userError: null,
      };

    case types.GET_MUTUAL_COMMUNITIES_SUCCESS:
      return {
        ...state,
        mutualCommunities: payload.mutualCommunities || [],
        mutualCommunitiesLoading: false,
        userError: null,
      };

    case types.GET_MUTUAL_COMMUNITIES_FAIL:
      return {
        ...state,
        mutualCommunitiesLoading: false,
        userError: payload,
      };

    case types.GET_PRIVACY_SETTINGS_SUCCESS:
      return {
        ...state,
        privacySettings: payload,
        userError: null,
      };

    case types.GET_PRIVACY_SETTINGS_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.UPDATE_PRIVACY_SETTINGS_SUCCESS:
      return {
        ...state,
        privacySettings: payload,
        userError: null,
      };

    case types.UPDATE_PRIVACY_SETTINGS_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.BLOCK_USER_SUCCESS:
      return {
        ...state,
        blockedUsers: [...state.blockedUsers, payload.userId],
        userError: null,
      };

    case types.BLOCK_USER_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.UNBLOCK_USER_SUCCESS:
      return {
        ...state,
        blockedUsers: state.blockedUsers.filter(id => id !== payload.userId),
        blockedUsersList: state.blockedUsersList.filter(user => user._id !== payload.userId),
        userError: null,
      };

    case types.UNBLOCK_USER_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.GET_BLOCKED_USERS_SUCCESS:
      return {
        ...state,
        blockedUsers: payload.map(user => user._id),
        blockedUsersList: payload, // Store full user objects
        userError: null,
      };

    case types.GET_BLOCKED_USERS_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.GET_RECEIVED_PROFILE_VIEW_REQUESTS_SUCCESS:
      if (payload.skip === 0) {
        return {
          ...state,
          receivedProfileViewRequests: payload.requests || [],
          totalReceivedProfileViewRequests: payload.total || 0,
          userError: null,
        };
      } else {
        return {
          ...state,
          receivedProfileViewRequests: [
            ...state.receivedProfileViewRequests,
            ...(payload.requests || []),
          ],
          totalReceivedProfileViewRequests: payload.total || 0,
          userError: null,
        };
      }

    case types.GET_RECEIVED_PROFILE_VIEW_REQUESTS_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.GET_SENT_PROFILE_VIEW_REQUESTS_SUCCESS:
      if (payload.skip === 0) {
        return {
          ...state,
          sentProfileViewRequests: payload.requests || [],
          totalSentProfileViewRequests: payload.total || 0,
          userError: null,
        };
      } else {
        return {
          ...state,
          sentProfileViewRequests: [
            ...state.sentProfileViewRequests,
            ...(payload.requests || []),
          ],
          totalSentProfileViewRequests: payload.total || 0,
          userError: null,
        };
      }

    case types.GET_SENT_PROFILE_VIEW_REQUESTS_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.ACCEPT_PROFILE_VIEW_REQUEST_SUCCESS:
      return {
        ...state,
        receivedProfileViewRequests: state.receivedProfileViewRequests.map(req =>
          req._id === payload.request._id ? payload.request : req
        ),
        userError: null,
      };

    case types.ACCEPT_PROFILE_VIEW_REQUEST_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.DENY_PROFILE_VIEW_REQUEST_SUCCESS:
      return {
        ...state,
        receivedProfileViewRequests: state.receivedProfileViewRequests.map(req =>
          req._id === payload.request._id ? payload.request : req
        ),
        userError: null,
      };

    case types.DENY_PROFILE_VIEW_REQUEST_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.CREATE_PROFILE_VIEW_REQUEST_SUCCESS:
      return {
        ...state,
        sentProfileViewRequests: [payload.request, ...state.sentProfileViewRequests],
        totalSentProfileViewRequests: state.totalSentProfileViewRequests + 1,
        userError: null,
      };

    case types.CREATE_PROFILE_VIEW_REQUEST_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.CLEAR_RECEIVED_PROFILE_VIEW_REQUESTS:
      return {
        ...state,
        receivedProfileViewRequests: [],
        totalReceivedProfileViewRequests: 0,
      };

    case types.CLEAR_SENT_PROFILE_VIEW_REQUESTS:
      return {
        ...state,
        sentProfileViewRequests: [],
        totalSentProfileViewRequests: 0,
      };

    case types.REVOKE_PROFILE_VIEW_REQUEST_SUCCESS:
      return {
        ...state,
        receivedProfileViewRequests: state.receivedProfileViewRequests.map(req =>
          req._id === payload.request._id ? payload.request : req
        ),
        userError: null,
      };

    case types.REVOKE_PROFILE_VIEW_REQUEST_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.WITHDRAW_PROFILE_VIEW_REQUEST_SUCCESS:
      return {
        ...state,
        sentProfileViewRequests: state.sentProfileViewRequests.filter(req => req._id !== payload.requestId),
        totalSentProfileViewRequests: Math.max(0, (state.totalSentProfileViewRequests || 0) - 1),
        userError: null,
      };

    case types.WITHDRAW_PROFILE_VIEW_REQUEST_FAIL:
      return {
        ...state,
        userError: payload,
      };

    // Follow Request Cases
    case types.GET_RECEIVED_FOLLOW_REQUESTS_SUCCESS:
      return {
        ...state,
        receivedFollowRequests: payload.requests || [],
        totalReceivedFollowRequests: payload.total || 0,
        userError: null,
      };

    case types.GET_RECEIVED_FOLLOW_REQUESTS_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.GET_SENT_FOLLOW_REQUESTS_SUCCESS:
      // If skip is 0, replace the array; otherwise append for pagination
      const shouldReplaceFollow = action.skip === 0 || action.skip === undefined;
      return {
        ...state,
        sentFollowRequests: shouldReplaceFollow
          ? (payload.requests || [])
          : [
              ...state.sentFollowRequests,
              ...(payload.requests || []),
            ],
        totalSentFollowRequests: payload.total || 0,
        userError: null,
      };

    case types.GET_SENT_FOLLOW_REQUESTS_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.ACCEPT_FOLLOW_REQUEST_SUCCESS:
      return {
        ...state,
        receivedFollowRequests: state.receivedFollowRequests.map(req =>
          req._id === payload.request._id ? payload.request : req
        ),
        userError: null,
      };

    case types.ACCEPT_FOLLOW_REQUEST_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.DENY_FOLLOW_REQUEST_SUCCESS:
      return {
        ...state,
        receivedFollowRequests: state.receivedFollowRequests.map(req =>
          req._id === payload.request._id ? payload.request : req
        ),
        userError: null,
      };

    case types.DENY_FOLLOW_REQUEST_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.WITHDRAW_FOLLOW_REQUEST_SUCCESS:
      return {
        ...state,
        sentFollowRequests: state.sentFollowRequests.filter(req => req._id !== payload.requestId),
        totalSentFollowRequests: Math.max(0, (state.totalSentFollowRequests || 0) - 1),
        userError: null,
      };

    case types.WITHDRAW_FOLLOW_REQUEST_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.CREATE_FOLLOW_REQUEST_SUCCESS:
      return {
        ...state,
        sentFollowRequests: [payload.request, ...state.sentFollowRequests],
        totalSentFollowRequests: state.totalSentFollowRequests + 1,
        userError: null,
      };

    case types.CREATE_FOLLOW_REQUEST_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.CLEAR_RECEIVED_FOLLOW_REQUESTS:
      return {
        ...state,
        receivedFollowRequests: [],
        totalReceivedFollowRequests: 0,
      };

    case types.CLEAR_SENT_FOLLOW_REQUESTS:
      return {
        ...state,
        sentFollowRequests: [],
        totalSentFollowRequests: 0,
      };

    // Notification Cases
    case types.GET_NOTIFICATIONS_SUCCESS:
      const shouldReplaceNotifications = action.skip === 0 || action.skip === undefined;
      return {
        ...state,
        notifications: shouldReplaceNotifications
          ? (payload.notifications || [])
          : [...state.notifications, ...(payload.notifications || [])],
        totalNotifications: payload.total || 0,
        unreadNotificationCount: payload.unreadCount || 0,
        userError: null,
      };

    case types.GET_NOTIFICATIONS_FAIL:
      return {
        ...state,
        userError: payload,
      };

    case types.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [payload, ...state.notifications],
        unreadNotificationCount: (state.unreadNotificationCount || 0) + 1,
        totalNotifications: (state.totalNotifications || 0) + 1,
      };

    case types.MARK_NOTIFICATION_READ_SUCCESS:
      return {
        ...state,
        notifications: state.notifications.map((notif) =>
          notif._id === payload.notificationId
            ? { ...notif, isRead: true, readAt: new Date() }
            : notif
        ),
        unreadNotificationCount: Math.max(0, (state.unreadNotificationCount || 0) - 1),
      };

    case types.MARK_ALL_NOTIFICATIONS_READ_SUCCESS:
      return {
        ...state,
        notifications: state.notifications.map((notif) => ({
          ...notif,
          isRead: true,
          readAt: new Date(),
        })),
        unreadNotificationCount: 0,
      };

    case types.DELETE_NOTIFICATION_SUCCESS:
      const deletedNotif = state.notifications.find(
        (n) => n._id === payload.notificationId
      );
      return {
        ...state,
        notifications: state.notifications.filter(
          (n) => n._id !== payload.notificationId
        ),
        totalNotifications: Math.max(0, (state.totalNotifications || 0) - 1),
        unreadNotificationCount: deletedNotif && !deletedNotif.isRead
          ? Math.max(0, (state.unreadNotificationCount || 0) - 1)
          : state.unreadNotificationCount,
      };

    case types.UPDATE_NOTIFICATION_COUNT:
      return {
        ...state,
        unreadNotificationCount: payload,
      };

    default:
      return state;
  }
};

export default userReducer;
